import { SelectorChain, SelectorPart } from '@fullstory/proto/fs/selectors';
import { serializeSelectorPart } from './selector_parser';
import { SelectorChainRule } from './selector';

/**
 * SelectorMatcher allows matching selector parts given a parsed selector chain.
 * The selector will be matched according to standard CSS rules and does not require that *each* part matches the
 * selector chain as long as all parts satisfy the selector chain matcher.
 * Note: Only ' ' (any) and '>' (child) combinators are supported. Any other combinators in the selector chain matcher
 *       will result in no match.
 */
export class SelectorMatcher {
  private sacrificialElement: HTMLDivElement;

  public constructor() {
    this.sacrificialElement = document.createElement('div');
  }

  /**
   * Determines whether the selector chain matches the ordered parts.
   */
  public matches(leafMatcher: SelectorChain, selector: SelectorPart[], mustMatch: boolean = false): boolean {
    return this.matchesRecursive(leafMatcher, selector, selector.length - 1, mustMatch);
  }

  /**
   * Determines whether the selector chain rules match the ordered parts.
   * Returns first matched depths (if applicable) for the query rules.
   */
  public matchesWithDepths(queryRules: SelectorChainRule[], selector: SelectorPart[], fullDepthMatch: boolean = false): { matches: boolean, depths: number[] } {
    const depths = Array<number>(queryRules.length).fill(0);
    const matches = this.matchesWithDepthsRecursive(queryRules, 0, selector, 0, false, depths);
    if (matches && fullDepthMatch && (depths[depths.length - 1] !== selector.length - 1)) {
      return { matches: false, depths: [] };
    }
    return { matches, depths };
  }

  private matchesRecursive(currentMatcher: SelectorChain, selector: SelectorPart[], partIdx: number, mustMatch: boolean): boolean {
    if (!currentMatcher.rule || partIdx < 0) {
      // Did not completely match and there is nothing left to try.
      return false;
    }

    const rule = currentMatcher.rule;
    const part = selector[partIdx];
    if (part && this.matchPart(rule, part)) {
      if (!currentMatcher.parent) {
        // We are done. We matched everything!
        return true;
      }
      // Otherwise, we matched this matcher, let's move to the parent and keep going based on the combinator.
      switch (currentMatcher.combinator) {
        case '':
        case ' ':
          // Common any descendent combinator.
          return this.matchesRecursive(currentMatcher.parent, selector, partIdx - 1, false);
        case '>':
          // Child combinator.
          // Parent must match exactly. If not, we can still keep going with the current matcher, just a level up
          // since the combinator makes the matcher context specific.
          if (this.matchesRecursive(currentMatcher.parent, selector, partIdx - 1, true)) {
            return true;
          } else if (mustMatch) {
            // mustMatch was marked and we didn't match, exit early
            return false;
          } else {
            return this.matchesRecursive(currentMatcher, selector, partIdx - 1, false);
          }
        default:
          return false;
      }
    } else if (mustMatch) {
      // mustMatch was marked and we didn't match, exit early
      return false;
    } else {
      // We didn't match, we can keep the current matcher and just move to the next part.
      return this.matchesRecursive(currentMatcher, selector, partIdx - 1, false);
    }
  }

  private matchesWithDepthsRecursive(queryRules: SelectorChainRule[], queryRuleIdx: number,
    parts: SelectorPart[], partIdx: number,
    mustMatch: boolean, depths: number[]): boolean {
    const rule = queryRules[queryRuleIdx];
    const part = parts[partIdx];
    if (!rule || !part) {
      // Did not completely match and there is nothing left to try.
      return false;
    }

    if (this.matchPart(rule.rule, part)) {
      // No matter what, when we match the part, assign the part depth relative to the query rule index.
      depths[queryRuleIdx] = partIdx;
      // Then check if we are done or need to continue.
      if (queryRuleIdx === queryRules.length - 1) {
        // We are done. We matched everything!
        return true;
      }
      // Otherwise, we matched this matcher, let's move to the descendent and keep going based on the combinator.
      switch (rule.combinator) {
        case '':
        case ' ':
          // Common any descendent combinator.
          return this.matchesWithDepthsRecursive(queryRules, queryRuleIdx + 1, parts, partIdx + 1, false, depths);
        case '>':
          // Child combinator.
          // Descendent must match exactly. If not, we can still keep going with the current matcher, just a level up
          // since the combinator makes the matcher context specific.
          if (this.matchesWithDepthsRecursive(queryRules, queryRuleIdx + 1, parts, partIdx + 1, true, depths)) {
            return true;
          } else if (mustMatch) {
            // mustMatch was marked and we didn't match, exit early
            return false;
          } else {
            return this.matchesWithDepthsRecursive(queryRules, queryRuleIdx, parts, partIdx + 1, false, depths);
          }
        default:
          return false;
      }
    } else if (mustMatch) {
      // mustMatch was marked and we didn't match, exit early
      return false;
    } else {
      // We didn't match, we can keep the current matcher and just move to the next part.
      return this.matchesWithDepthsRecursive(queryRules, queryRuleIdx, parts, partIdx + 1, false, depths);
    }
  }

  /**
   * Determines whether the selector part matches the single part.
   */
  public matchPart(matcher: SelectorPart | null, part: SelectorPart): boolean {
    if (!matcher) {
      return false;
    }

    // Check the tag first as the sacrificial element is always a div.
    if (matcher.tag && matcher.tag !== '*') {
      if (matcher.tag.toLowerCase() !== part.tag.toLowerCase()) {
        return false;
      }
    }

    // Set the rest of the attributes on the sacrificial element.
    const oldAttrs = Array.from(this.sacrificialElement.attributes);
    oldAttrs.forEach(attr => this.sacrificialElement.removeAttribute(attr.name));
    if (part.attrs) {
      Object.entries(part.attrs).forEach(([attr, value]) => {
        try {
          this.sacrificialElement.setAttribute(attr, value);
        } catch (e) {
          // Ignore errors when setting attributes for the purposes of selector matching.
        }
      });
    }
    this.sacrificialElement.id = part.id;

    // We will always set the classes on the element in case of any class matchers.
    this.sacrificialElement.className = (part.classes ?? []).join(' ');
    // But if we can match all of the classes directly up front, we will do so.
    // (to better handle some unique cases like native mobile classes with spaces)
    // TODO: we probably need a more robust selector part matching solution for some of these known use cases
    let partClassSet: Set<string>;
    const classesMatch = matcher.classes.every(clazz => {
      if (!partClassSet) partClassSet = new Set(part.classes ?? []);
      return partClassSet.has(clazz);
    });

    // We want to ensure that the selector matches our sacrificial element
    const oldTag = matcher.tag;
    matcher.tag = '*';
    const oldClasses = matcher.classes;
    if (classesMatch) {
      // no need to match classes w/ the native selector matching
      matcher.classes = [];
    }
    const match = this.sacrificialElement.matches(serializeSelectorPart(matcher));
    matcher.tag = oldTag;
    matcher.classes = oldClasses;

    return match;
  }
}
